import { useEffect, useRef, useState } from 'react';

const items = [
  {
    icon: 'ri-book-open-line',
    emoji: '📘',
    title: 'Ebook Profesional Paso a Paso',
    desc: 'Guía estructurada para reorganizar las finanzas de tu familia. Con ejercicios prácticos para cada etapa del proceso.',
    value: '$27',
    color: 'bg-emerald-50 border-emerald-200',
    iconColor: 'text-emerald-700',
    iconBg: 'bg-emerald-100',
  },
  {
    icon: 'ri-smartphone-line',
    emoji: '📱',
    title: 'App Interactiva con Plan de 30 Días',
    desc: 'Incluye tareas prácticas, seguimiento de progreso y ejercicios familiares que puedes completar día a día.',
    value: '$37',
    color: 'bg-amber-50 border-amber-200',
    iconColor: 'text-amber-700',
    iconBg: 'bg-amber-100',
  },
  {
    icon: 'ri-robot-line',
    emoji: '🧠',
    title: 'Mentor GPT en Finanzas Familiares',
    desc: 'Responde tus dudas y te ayuda a aplicar las estrategias según tu situación específica. Disponible 24/7.',
    value: '$19',
    color: 'bg-rose-50 border-rose-200',
    iconColor: 'text-rose-700',
    iconBg: 'bg-rose-100',
  },
  {
    icon: 'ri-headphone-line',
    emoji: '🎧',
    title: 'Audiolibro + Formatos Digitales',
    desc: 'Aprende mientras trabajas, conduces o realizas tus actividades diarias. Adaptable a tu estilo de vida.',
    value: '$14',
    color: 'bg-violet-50 border-violet-200',
    iconColor: 'text-violet-700',
    iconBg: 'bg-violet-100',
  },
];

const steps = [
  { num: '01', title: 'Accede al sistema', desc: 'Descarga todos los materiales inmediatamente después de tu compra.' },
  { num: '02', title: 'Evalúa tu situación', desc: 'Analiza ingresos, gastos y deudas familiares con las herramientas incluidas.' },
  { num: '03', title: 'Sigue el plan diario', desc: 'Ejecuta las tareas y ejercicios del plan de 30 días con la app interactiva.' },
  { num: '04', title: 'Construye nuevos hábitos', desc: 'Establece rutinas financieras saludables que toda la familia puede mantener.' },
];

export default function ComoFuncionaSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-white py-20 md:py-28 px-6 md:px-12" ref={ref}>
      <div className="max-w-6xl mx-auto">
        {/* Sistema Completo */}
        <div
          className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="inline-block text-emerald-700 text-xs font-bold tracking-widest uppercase mb-4">
            Sistema Completo ElaBooks
          </span>
          <h2
            className="text-3xl md:text-5xl font-bold text-stone-900 leading-tight mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            No compras solo información.{' '}
            <span className="text-emerald-700">Obtienes un sistema completo.</span>
          </h2>
          <p className="text-stone-500 text-lg max-w-xl mx-auto">
            Todo lo que necesitas para transformar las finanzas de tu familia en un solo paquete.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
          {items.map((item, i) => (
            <div
              key={i}
              className={`${item.color} border rounded-2xl p-6 flex flex-col gap-4 transition-all duration-600 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${item.iconBg}`}>
                <i className={`${item.icon} ${item.iconColor} text-xl`} />
              </div>
              <div>
                <p className="text-2xl mb-1">{item.emoji}</p>
                <h3 className="font-bold text-stone-900 text-sm leading-snug mb-2">{item.title}</h3>
                <p className="text-stone-500 text-xs leading-relaxed">{item.desc}</p>
              </div>
              <div className="mt-auto pt-2 border-t border-stone-200">
                <span className="text-stone-400 text-xs">Valor estimado</span>
                <span className="ml-2 font-bold text-stone-700 text-sm">{item.value}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Cómo funciona - steps */}
        <div
          className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="text-center mb-12">
            <span className="inline-block text-emerald-700 text-xs font-bold tracking-widest uppercase mb-4">
              Cómo funciona
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold text-stone-900 leading-tight"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Tu camino hacia la estabilidad financiera
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-emerald-700 text-white font-bold text-sm flex-shrink-0">
                    {step.num}
                  </div>
                  {i < steps.length - 1 && (
                    <div className="flex-1 h-px bg-emerald-200 hidden lg:block" />
                  )}
                </div>
                <div>
                  <h4 className="font-bold text-stone-900 text-base mb-1.5">{step.title}</h4>
                  <p className="text-stone-500 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
